import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getTemplateVersions,
  getLearningEvents,
  learnNow,
  activateTemplateVersion,
  updateTemplateSections,
  getWritingTypes,
} from 'services/escriturasTemplatesService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { useSnackbar } from 'context/SnackbarContext';
import { useNotificationsRealtime } from 'context/notifications/NotificationsRealtimeContext';
import { escriturasTemplatesI18n } from './i18n';
import TemplateSectionsEditor from './TemplateSectionsEditor';
import WritingTypeDocumentsPanel from './WritingTypeDocumentsPanel';
import TemplateCreateModal from './TemplateCreateModal';
import TemplateSideIndex from './TemplateSideIndex';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { htmlToText, diffTokens } from './templateDiff';
import './escriturasTemplates.css';

const parseSections = (sectionsJson) => {
  if (!sectionsJson) return null;
  try {
    const parsed = JSON.parse(sectionsJson);
    return parsed?.sections ? parsed : null;
  } catch {
    return null;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

/** Tab "Documento": ensambla los machotes en orden, como vista previa limpia del escrito.
 *  Sidebar de secciones a la derecha; condicionales con chip amigable (no línea suelta). */
function DocumentPreview({ sections, t }) {
  if (!sections)
    return (
      <p className='font-light' style={{ fontSize: 13 }}>
        {t('tpl_detail_no_sections')}
      </p>
    );
  const ordered = sections.sections
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const items = ordered.map((s) => ({
    id: `doc-${s.key}`,
    label: s.title,
    conditional: !!s.when,
    title: s.when || '',
  }));
  return (
    <div className='row'>
      <div className='col-12 col-lg-9'>
        <div
          style={{
            fontSize: 12,
            color: '#475569',
            background: '#F1F5F9',
            border: '1px solid #E2E8F0',
            borderRadius: 6,
            padding: '6px 12px',
            marginBottom: 12,
          }}
        >
          {t('tpl_preview_banner')}
        </div>
        <div
          style={{
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            padding: '40px 56px',
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 14,
            lineHeight: 1.8,
            color: '#1E293B',
          }}
        >
          {ordered.map((s) => (
            <div
              id={`doc-${s.key}`}
              key={s.key}
              style={{ marginBottom: 14, scrollMarginTop: 12 }}
            >
              {s.when && (
                <span
                  title={s.when}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 10.5,
                    fontFamily: 'sans-serif',
                    color: '#92400E',
                    background: '#FEF9C3',
                    border: '1px solid #FDE68A',
                    borderRadius: 10,
                    padding: '1px 8px',
                    marginBottom: 6,
                  }}
                >
                  ◐ {t('tpl_section_conditional')}
                </span>
              )}
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    s.content || `<p style="color:#CBD5E1">(${s.title})</p>`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className='col-12 col-lg-3'>
        <TemplateSideIndex items={items} />
      </div>
    </div>
  );
}

/** Renderiza tokens de diff: verde = agregado, rojo = quitado (tachado). Espacios sin color. */
function DiffText({ ops }) {
  return (
    <div className='tpl-diff'>
      {ops.map((o, i) => {
        if (o.t === 'eq' || /^\s+$/.test(o.v))
          return <span key={i}>{o.v}</span>;
        return (
          <span
            key={i}
            className={o.t === 'add' ? 'tpl-diff-add' : 'tpl-diff-del'}
          >
            {o.v}
          </span>
        );
      })}
    </div>
  );
}

const CHANGE_TYPE_STYLE = {
  added: { color: '#166534', background: '#DCFCE7', border: '#BBF7D0' },
  removed: { color: '#991B1B', background: '#FEE2E2', border: '#FECACA' },
  updated: { color: '#1E40AF', background: '#DBEAFE', border: '#BFDBFE' },
  when_changed: { color: '#92400E', background: '#FEF9C3', border: '#FDE68A' },
};

/** Diff de un evento: por cada sección cambiada, machote viejo vs nuevo en verde/rojo. */
function EventDiff({ event, versions, t }) {
  if (!event.toTemplateId) {
    return (
      <p className='font-light' style={{ fontSize: 13 }}>
        {t('tpl_diff_no_version')}
      </p>
    );
  }
  const toV = versions.find((v) => v.id === event.toTemplateId);
  const fromV = event.fromTemplateId
    ? versions.find((v) => v.id === event.fromTemplateId)
    : null;
  if (!toV) {
    return (
      <p className='font-light' style={{ fontSize: 13 }}>
        {t('tpl_diff_version_missing')}
      </p>
    );
  }

  const toSecs = parseSections(toV.sectionsJson);
  const fromSecs = parseSections(fromV?.sectionsJson);
  const toMap = new Map((toSecs?.sections || []).map((s) => [s.key, s]));
  const fromMap = new Map((fromSecs?.sections || []).map((s) => [s.key, s]));

  // Versión inicial (CREATE, sin versión previa): nada con qué comparar → resumen de secciones.
  if (!fromV) {
    const titles = (toSecs?.sections || [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return (
      <div>
        <p style={{ fontSize: 13, color: '#475569' }}>
          {t('tpl_diff_initial', {
            version: toV.version,
            count: titles.length,
          })}
        </p>
        <div className='d-flex flex-wrap gap-2'>
          {titles.map((s) => (
            <span
              key={s.key}
              className='tpl-diff-add'
              style={{ padding: '2px 8px', fontSize: 12 }}
            >
              {s.title}
            </span>
          ))}
        </div>
      </div>
    );
  }

  const changeMeta = new Map(
    (event.changes || []).map((c) => [c.sectionKey, c])
  );
  // Secciones a mostrar: las reportadas por el agente; si vienen vacías, las que difieren de hecho.
  let changedKeys;
  if (event.changes?.length) {
    changedKeys = event.changes.map((c) => c.sectionKey);
  } else {
    const keys = new Set([...fromMap.keys(), ...toMap.keys()]);
    changedKeys = [...keys].filter((k) => {
      const o = fromMap.get(k);
      const n = toMap.get(k);
      if (!o || !n) return true;
      return (
        (o.content || '') !== (n.content || '') ||
        (o.when || '') !== (n.when || '') ||
        (o.title || '') !== (n.title || '')
      );
    });
  }
  changedKeys = [...new Set(changedKeys)].sort(
    (a, b) =>
      (toMap.get(a)?.order ?? fromMap.get(a)?.order ?? 0) -
      (toMap.get(b)?.order ?? fromMap.get(b)?.order ?? 0)
  );

  if (changedKeys.length === 0) {
    return (
      <p className='font-light' style={{ fontSize: 13 }}>
        {t('tpl_diff_none')}
      </p>
    );
  }

  return (
    <div>
      {changedKeys.map((key) => {
        const oldS = fromMap.get(key);
        const newS = toMap.get(key);
        const meta = changeMeta.get(key);
        const changeType =
          meta?.changeType || (!oldS ? 'added' : !newS ? 'removed' : 'updated');
        const style =
          CHANGE_TYPE_STYLE[changeType] || CHANGE_TYPE_STYLE.updated;
        const title = newS?.title || oldS?.title || key;
        const contentOps = diffTokens(
          htmlToText(oldS?.content),
          htmlToText(newS?.content)
        );
        const whenChanged = (oldS?.when || '') !== (newS?.when || '');
        const whenOps = whenChanged
          ? diffTokens(oldS?.when || '', newS?.when || '')
          : null;
        return (
          <div
            key={key}
            style={{
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 10,
              background: '#fff',
            }}
          >
            <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
              <strong style={{ fontSize: 13 }}>{title}</strong>
              <span
                style={{
                  fontSize: 10.5,
                  fontFamily: 'sans-serif',
                  color: style.color,
                  background: style.background,
                  border: `1px solid ${style.border}`,
                  borderRadius: 10,
                  padding: '1px 8px',
                }}
              >
                {t(`tpl_diff_type_${changeType}`) || changeType}
              </span>
              <code style={{ fontSize: 11, color: '#94A3B8' }}>{key}</code>
            </div>
            {meta?.rationale && (
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>
                {meta.rationale}
              </div>
            )}
            {whenOps && (
              <div style={{ marginBottom: 6 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: '#94A3B8',
                    fontFamily: 'sans-serif',
                  }}
                >
                  {t('tpl_section_when')}
                </div>
                <DiffText ops={whenOps} />
              </div>
            )}
            <DiffText ops={contentOps} />
          </div>
        );
      })}
    </div>
  );
}

/** Tab "Aprendizaje": timeline de fechas a la derecha; click en una fecha → diff de ese evento. */
function LearningTab({ events, versions, t, Badge }) {
  const statusColor = (s) =>
    s === 'COMPLETED'
      ? 'success'
      : s === 'ERROR'
        ? 'error'
        : s === 'QUEUED'
          ? 'warning'
          : 'light';

  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    if (events.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) =>
      prev && events.some((e) => e.id === prev)
        ? prev
        : (events.find((e) => e.toTemplateId)?.id ?? events[0].id)
    );
  }, [events]);

  if (events.length === 0) {
    return (
      <p className='font-light' style={{ fontSize: 13 }}>
        {t('tpl_learning_empty')}
      </p>
    );
  }

  const selected = events.find((e) => e.id === selectedId) || null;

  return (
    <div className='row'>
      <div className='col-12 col-lg-9'>
        {selected ? (
          <>
            <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
              <strong>
                {t(`tpl_learning_trigger_${selected.triggerType}`) ||
                  selected.triggerType}
              </strong>
              {Badge ? (
                <Badge
                  text={
                    t(`tpl_learning_status_${selected.status}`) ||
                    selected.status
                  }
                  color={statusColor(selected.status)}
                  size='sm'
                />
              ) : (
                <span>{selected.status}</span>
              )}
              <span style={{ color: '#94A3B8', fontSize: 12 }}>
                {formatDate(selected.create_At)}
              </span>
            </div>
            {selected.rationale && (
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                {selected.rationale}
              </div>
            )}
            {selected.errorMessage && (
              <div style={{ fontSize: 13, color: '#DC2626', marginBottom: 8 }}>
                {selected.errorMessage}
              </div>
            )}
            <EventDiff event={selected} versions={versions} t={t} />
          </>
        ) : (
          <p className='font-light' style={{ fontSize: 13 }}>
            {t('tpl_diff_select_hint')}
          </p>
        )}
      </div>
      <div className='col-12 col-lg-3'>
        <TemplateSideIndex
          items={events.map((ev) => ({
            id: `ev-${ev.id}`,
            label: `${formatDate(ev.create_At)} · ${t(`tpl_learning_trigger_${ev.triggerType}`) || ev.triggerType}`,
            conditional: false,
          }))}
          activeId={selectedId != null ? `ev-${selectedId}` : null}
          onSelect={(id) =>
            setSelectedId(Number(String(id).replace('ev-', '')))
          }
        />
      </div>
    </div>
  );
}

/** CTA cuando el tipo aún no tiene plantilla generada (no hay versión). */
function EmptyTemplateCTA({ t, Button, onGenerate, canGenerate }) {
  return (
    <div className='text-center' style={{ padding: '32px 16px' }}>
      <p className='font-light' style={{ fontSize: 14, color: '#64748B' }}>
        {t('tpl_detail_no_template')}
      </p>
      {Button && canGenerate && (
        <Button
          label={t('tpl_action_create')}
          size='sm'
          variant='primary'
          onClick={onGenerate}
        />
      )}
    </div>
  );
}

export default function EscriturasTemplateDetail({ DesignSystemComponents }) {
  const { name } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tplLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: escriturasTemplatesI18n });
  const { showSnackbar } = useSnackbar();
  const { subscribeToTemplates } = useNotificationsRealtime();

  const { Button, Badge, Dropdown, Card, Modal } = DesignSystemComponents ?? {};

  const [versions, setVersions] = useState([]);
  const [events, setEvents] = useState([]);
  // Id + nombre del tipo resuelto por code (sirve aunque el tipo aún no tenga template).
  const [writingTypeId, setWritingTypeId] = useState(null);
  const [writingTypeName, setWritingTypeName] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [tab, setTab] = useState('documento');
  const [acting, setActing] = useState(false);
  // Operación con IA en curso para este template (single-flight): bloquea edición.
  const [generating, setGenerating] = useState(false);
  // Guard de salida: el editor de secciones reporta si tiene cambios sin guardar.
  const [sectionsDirty, setSectionsDirty] = useState(false);
  const [docsDirty, setDocsDirty] = useState(false); // cambios sin guardar en la tab Documentos
  const [leaveConfirm, setLeaveConfirm] = useState(null); // { run } | null
  const isDirty = sectionsDirty || docsDirty;

  useEffect(() => {
    escriturasTemplatesI18n.changeLanguage(tplLanguage);
  }, [tplLanguage]);

  // Advertencia del navegador (cerrar pestaña / refrescar) con cambios sin guardar.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Si hay cambios sin guardar, pide confirmación antes de ejecutar la acción de salida
  // (volver a la lista, cambiar de tab o de versión). Sin cambios → ejecuta directo.
  const guardedRun = (action) => {
    if (isDirty) setLeaveConfirm({ run: action });
    else action();
  };
  const requestTab = (next) => {
    if (next !== tab) guardedRun(() => setTab(next));
  };
  const requestVersion = (id) => {
    if (id !== selectedVersionId) guardedRun(() => setSelectedVersionId(id));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [v, e, types] = await Promise.all([
        getTemplateVersions(name),
        getLearningEvents(80),
        getWritingTypes(),
      ]);
      setVersions(v);
      const active = v.find((x) => x.isActive) || v[0];
      setSelectedVersionId((prev) => prev ?? active?.id ?? null);
      // Resolver el tipo por code (= name por convención) o por templateName; fallback a la versión.
      const wt = (types || []).find(
        (x) => x.code === name || x.templateName === name
      );
      setWritingTypeId(
        wt?.id ?? v.find((x) => x.writingTypeId)?.writingTypeId ?? null
      );
      setWritingTypeName(wt?.displayName ?? null);
      const mine = (e || []).filter((ev) => ev.templateName === name);
      setEvents(mine);
      setGenerating(mine.some((ev) => ev.status === 'PROCESSING'));
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('tpl_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [name, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Tiempo real: si otro usuario (o la cola) lanza/termina una operación con IA de ESTE
  // template, refleja el bloqueo al instante; al terminar, recarga versiones/eventos.
  useEffect(() => {
    const unsubscribe = subscribeToTemplates(
      ({ templateName, generating: g }) => {
        if (templateName !== name) return;
        setGenerating(!!g);
        if (!g) load();
      }
    );
    return unsubscribe;
  }, [subscribeToTemplates, name, load]);

  const selected = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) || null,
    [versions, selectedVersionId]
  );
  const sections = useMemo(
    () => parseSections(selected?.sectionsJson),
    [selected]
  );
  const queuedCount = events.filter((e) => e.status === 'QUEUED').length;
  const description = selected?.description;

  const versionOptions = versions.map((v) => ({
    label: `v${v.version}${v.isActive ? ` (${t('tpl_detail_active')})` : ''} — ${v.sourceType}`,
    value: String(v.id),
  }));
  const selectedVersionOption =
    versionOptions.find((o) => o.value === String(selectedVersionId)) || null;

  const handleActivate = async () => {
    if (!selected || selected.isActive) return;
    setActing(true);
    try {
      await activateTemplateVersion(selected.id);
      showSnackbar?.(
        `v${selected.version} ${t('tpl_detail_active')}`,
        'success'
      );
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('tpl_error_generic')
      );
    } finally {
      setActing(false);
    }
  };

  const handleLearnNow = async () => {
    setActing(true);
    try {
      await learnNow(name);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('tpl_error_generic')
      );
    } finally {
      setActing(false);
    }
  };

  const handleSaveSections = async (updatedSections) => {
    setActing(true);
    setError(null);
    try {
      const result = await updateTemplateSections(
        name,
        JSON.stringify(updatedSections)
      );
      if (result?.status === 'ERROR') {
        setError(result.message || t('tpl_error_generic'));
        return false;
      }
      showSnackbar?.(
        t('tpl_create_success', { version: result?.template?.version }),
        'success'
      );
      setSelectedVersionId(result?.template?.id ?? null);
      await load();
      return true;
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('tpl_error_generic')
      );
      return false;
    } finally {
      setActing(false);
    }
  };

  const TABS = [
    { key: 'documento', label: t('tpl_tab_document') },
    {
      key: 'secciones',
      label: `${t('tpl_detail_sections')}${sections ? ` (${sections.sections.length})` : ''}`,
    },
    { key: 'documentos', label: t('tpl_typedocs_action') },
    {
      key: 'aprendizaje',
      label: `${t('tpl_detail_learning')} (${events.length})`,
    },
  ];

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      {/* Header: título = code, descripción debajo */}
      <button
        type='button'
        className='btn btn-link btn-sm p-0 mb-1'
        onClick={() => guardedRun(() => navigate('/escrituras/templates'))}
      >
        ← {t('tpl_title')}
      </button>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{name}</h2>
          <h4 className='fw-normal'>{description || '—'}</h4>
        </div>
        <div className='d-flex gap-2 align-items-center'>
          {versionOptions.length > 0 &&
            (Dropdown ? (
              <div style={{ minWidth: 240 }}>
                <Dropdown
                  options={versionOptions}
                  value={selectedVersionOption}
                  onChange={(o) =>
                    requestVersion(o?.value ? Number(o.value) : null)
                  }
                  width='100%'
                />
              </div>
            ) : (
              <select
                className='form-select form-select-sm'
                value={selectedVersionId ?? ''}
                onChange={(e) => requestVersion(Number(e.target.value))}
              >
                {versionOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ))}
          {selected &&
            !selected.isActive &&
            (Button ? (
              <Button
                label={t('tpl_detail_activate')}
                size='sm'
                variant='primary'
                onClick={handleActivate}
                disabled={acting || generating}
              />
            ) : (
              <button
                type='button'
                className='btn btn-primary btn-sm'
                onClick={handleActivate}
                disabled={acting || generating}
              >
                {t('tpl_detail_activate')}
              </button>
            ))}
          {queuedCount > 0 &&
            (Button ? (
              <Button
                label={`${t('tpl_detail_learn_now')} (${queuedCount})`}
                size='sm'
                variant='secondary'
                onClick={handleLearnNow}
                disabled={acting || generating}
              />
            ) : (
              <button
                type='button'
                className='btn btn-outline-primary btn-sm'
                onClick={handleLearnNow}
                disabled={acting || generating}
              >
                {t('tpl_detail_learn_now')} ({queuedCount})
              </button>
            ))}
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {generating && (
          <div
            className='d-flex align-items-center gap-2 mb-3'
            style={{
              fontSize: 13,
              color: '#92400E',
              background: 'var(--warning-100, #fff3cd)',
              border: '1px solid var(--warning-200, #ffe69c)',
              borderRadius: 6,
              padding: '8px 12px',
            }}
          >
            <span
              className='spinner-border spinner-border-sm'
              role='status'
              aria-hidden='true'
              style={{ color: '#92400E' }}
            />
            {t('tpl_generating_banner')}
          </div>
        )}

        {/* Tabs estilo DataManagement (underline, sin botones) */}
        <nav
          role='tablist'
          className='mb-3'
          style={{
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            gap: 24,
          }}
        >
          {TABS.map((tb) => {
            const active = tab === tb.key;
            return (
              <button
                key={tb.key}
                type='button'
                role='tab'
                aria-selected={active}
                onClick={() => requestTab(tb.key)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px 2px',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: active ? 600 : 400,
                  color: active ? 'var(--primary-500, #2563EB)' : '#64748B',
                  borderBottom: active
                    ? '2px solid var(--primary-500, #2563EB)'
                    : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {tb.label}
              </button>
            );
          })}
        </nav>

        {loading ? (
          <p className='font-light'>{t('tpl_loading')}</p>
        ) : (
          <>
            {tab === 'documento' &&
              (versions.length === 0 ? (
                <EmptyTemplateCTA
                  t={t}
                  Button={Button}
                  canGenerate={!!writingTypeId}
                  onGenerate={() => setShowCreate(true)}
                />
              ) : (
                <DocumentPreview sections={sections} t={t} />
              ))}
            {tab === 'secciones' &&
              (versions.length === 0 ? (
                <EmptyTemplateCTA
                  t={t}
                  Button={Button}
                  canGenerate={!!writingTypeId}
                  onGenerate={() => setShowCreate(true)}
                />
              ) : sections ? (
                <TemplateSectionsEditor
                  key={selected?.id}
                  DesignSystemComponents={DesignSystemComponents}
                  initialSections={sections}
                  t={t}
                  saving={acting || generating}
                  onSave={handleSaveSections}
                  onDirtyChange={setSectionsDirty}
                />
              ) : (
                <p className='font-light' style={{ fontSize: 13 }}>
                  {t('tpl_detail_no_sections')}
                </p>
              ))}
            {tab === 'documentos' &&
              (writingTypeId ? (
                <WritingTypeDocumentsPanel
                  DesignSystemComponents={DesignSystemComponents}
                  writingTypeId={writingTypeId}
                  t={t}
                  onDirtyChange={setDocsDirty}
                />
              ) : (
                <p className='font-light' style={{ fontSize: 13 }}>
                  {loading ? t('tpl_loading') : t('tpl_typedocs_inactive')}
                </p>
              ))}
            {tab === 'aprendizaje' && (
              <LearningTab
                events={events}
                versions={versions}
                t={t}
                Badge={Badge}
              />
            )}
          </>
        )}

        {/* Guard de cambios sin guardar al salir (volver / cambiar tab / cambiar versión) */}
        <ConfirmModal
          Modal={Modal}
          show={!!leaveConfirm}
          title={t('tpl_unsaved_title')}
          message={t('tpl_unsaved_message')}
          confirmLabel={t('tpl_unsaved_discard')}
          cancelLabel={t('tpl_unsaved_keep')}
          confirmVariant='danger'
          onConfirm={() => {
            const action = leaveConfirm?.run;
            setSectionsDirty(false);
            setDocsDirty(false);
            setLeaveConfirm(null);
            action?.();
          }}
          onClose={() => setLeaveConfirm(null)}
        />

        {showCreate && writingTypeId && (
          <TemplateCreateModal
            DesignSystemComponents={DesignSystemComponents}
            writingType={{
              id: writingTypeId,
              code: name,
              displayName: writingTypeName || name,
              hasActiveTemplate: versions.length > 0,
              activeVersion: 0,
            }}
            t={t}
            onClose={() => setShowCreate(false)}
            onCreated={(result) => {
              setShowCreate(false);
              if (result?.template?.version)
                showSnackbar?.(
                  t('tpl_create_success', { version: result.template.version }),
                  'success'
                );
              setTab('documento');
              load();
            }}
          />
        )}
      </ContentWrapper>
    </div>
  );
}
